import { keyBy } from 'lodash';
import moment from 'moment';
import { ProjectPhase, ProjectPhaseMap } from 'api/project';
import { StatusFieldOption, StatusGroup } from 'api/tasks';

const totalPhasesLimit = 10;
const olderPhasesLimit = 4;

/**
 * @description Get the phases list limited to the totalPhasesLimit and olderPhasesLimit.
 * If total phases count <= totalPhasesLimit, then return them as it is
 * Else get the olderphases list limited <= to the olderPhasesLimit and then newerphases = totalPhasesLimit - olderPhases
 * @param phasesList
 */
const getLimitedPhases = (phasesList: ProjectPhase[]): ProjectPhase[] => {
  const sortedPhases = [...phasesList].sort((firstPhase, secondPhase) =>
    moment(firstPhase.startDate ?? undefined).diff(
      moment(secondPhase.startDate ?? undefined),
      'days',
    ),
  );

  if (sortedPhases && sortedPhases.length > totalPhasesLimit) {
    const now = moment();

    let olderPhases = sortedPhases.filter(
      (phase) => moment(phase.startDate ?? undefined) <= now,
    );
    let newerPhases = sortedPhases.filter(
      (phase) => moment(phase.startDate ?? undefined) > now,
    );

    const olderPhasesLength = olderPhases.length;
    const newerPhasesLength = newerPhases.length;

    // if there are no NewerPhases then limit older plases to totalPhasesLimit
    if (olderPhasesLength && !newerPhasesLength) {
      olderPhases = olderPhases.slice(0, totalPhasesLimit);
    } else if (!olderPhasesLength && newerPhasesLength) {
      newerPhases = newerPhases.slice(0, totalPhasesLimit);
    }
    // if the phases before (olderPhases) current date are < 4, then newerPhases is from 0, 10 - olderPhaseLength
    else if (olderPhasesLength <= olderPhasesLimit) {
      newerPhases = newerPhases.slice(0, totalPhasesLimit - olderPhasesLength);
    } else if (olderPhasesLength > olderPhasesLimit) {
      if (newerPhasesLength <= totalPhasesLimit - olderPhasesLimit) {
        olderPhases = olderPhases.slice(
          0,
          totalPhasesLimit - newerPhasesLength,
        );
      } else if (newerPhasesLength > totalPhasesLimit - olderPhasesLimit) {
        olderPhases = olderPhases.slice(0, olderPhasesLimit);
        newerPhases = newerPhases.slice(0, totalPhasesLimit - olderPhasesLimit);
      }
    }

    return [...olderPhases, ...newerPhases];
  }

  return sortedPhases;
};

export const getTransformedPhases = (
  data?: ProjectPhase[],
): ProjectPhaseMap[] => {
  if (data == null) {
    return [];
  }

  const limitedPhases = getLimitedPhases(data);

  return limitedPhases.map((phase) => {
    const statusLookupByGroup = keyBy(phase.progressStatus ?? [], 'group');
    /* TODO::FIX_ME we should use hardcoded fieldId for phase status */
    const phaseStatus = phase.fields.find(({ fieldId }) => fieldId === 1)!;

    return {
      id: phase.projectPhaseId,
      name: phase.projectPhaseName,
      inProgress: statusLookupByGroup[StatusGroup.InProgress]?.count ?? 0,
      completed: statusLookupByGroup[StatusGroup.Completed]?.count ?? 0,
      total: phase.totalTaskCount || 0,
      startDate: phase.startDate || '',
      status: phaseStatus?.metaFieldValue as StatusFieldOption, // TODO::FIX_ME  shouldn't use metaFieldValue and properly type
      private: phase?.private,
    };
  });
};

const getSortedPhases = (phasesList: ProjectPhase[]): ProjectPhase[] => {
  const sortedPhases = [...phasesList].sort((firstPhase, secondPhase) =>
    moment(firstPhase.startDate ?? undefined).diff(
      moment(secondPhase.startDate ?? undefined),
      'days',
    ),
  );

  return sortedPhases;
};

export const getTransformedPhasesForProjectSummary = (
  data?: ProjectPhase[],
): ProjectPhaseMap[] => {
  if (data == null) {
    return [];
  }

  const finalPhases = getSortedPhases(data);

  return finalPhases?.map((phase) => {
    /* TODO::FIX_ME we should use hardcoded fieldId for phase status */
    const phaseStatus = phase.fields.find(({ fieldId }) => fieldId === 1)!;

    return {
      id: phase.projectPhaseId,
      name: phase.projectPhaseName,
      inProgress:
        phase?.progressStatus?.find(
          (status) => status.group === StatusGroup.InProgress,
        )?.count ?? 0,
      completed:
        phase?.progressStatus?.find(
          (status) => status.group === StatusGroup.Completed,
        )?.count ?? 0,
      total: phase.totalTaskCount || 0,
      startDate: phase.startDate || '',
      status: phaseStatus?.metaFieldValue as StatusFieldOption, // TODO::FIX_ME  shouldn't use metaFieldValue and properly type
      private: phase?.private,
    };
  });
};
